// client/src/components/pages/RouteIntelligence/MonitorDetailModal.tsx (converted from .js, ST-00229)
//
// ANL-14: the Dispatch / Receipt columns show the matched pairs the variance is computed from,
// with the day totals labelled "Day total" underneath and the loads that did not match. Days not
// recalculated since migration 055 have no matched figures and show the day totals alone.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Mail, CheckCircle, MessageSquare, Calendar } from 'lucide-react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import api from '../../../services/api';
import {
  getVarianceState,
  varianceStates,
  formatVariance,
  formatWeight,
  formatDate,
  formatDateShort,
  DAY_RANGES,
} from './varianceConfig';
import AcknowledgeModal from './AcknowledgeModal';
import AlertLogModal from './AlertLogModal';
import { useThemeTokens } from '../../../utils/themeTokens';
import { matchedFigures, type MatchedFigures } from './varianceMatched';
import type {
  VarianceAcknowledgment,
  VarianceDashboardMonitor,
  VarianceResultRow,
  VarianceResultSummary,
} from '../../../types/clientPages/routeIntelligence';

/** One bar of the variance chart. */
interface ChartPoint {
  date: string;
  dateLabel: string;
  variance: number | null;
  isBreaching: boolean;
  streakDay: number;
  dispatchWeight: number;
  receiptWeight: number;
  matched: MatchedFigures | null;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: ChartPoint }>;
  threshold: number;
}

type ThemeTokens = ReturnType<typeof useThemeTokens>;

const rowMatched = (r: VarianceResultRow): MatchedFigures | null =>
  matchedFigures({
    matchedDispatchWeight: r.matched_dispatch_weight,
    matchedReceiptWeight: r.matched_receipt_weight,
    matchedCount: r.matched_count,
    dispatchCount: r.dispatch_count,
    receiptCount: r.receipt_count,
  });

const hasDayData = (r: VarianceResultRow): boolean =>
  Number(r.dispatch_weight) > 0 || Number(r.receipt_weight) > 0;

const ChartTooltip = ({ active, payload, threshold }: ChartTooltipProps) => {
  const d = payload?.[0]?.payload;
  if (!active || !d) return null;
  return (
    <div className="bg-surface border border-gray-200 rounded-md px-3 py-2 shadow-lg text-[11px]">
      <div className="text-slate-800 font-medium mb-1">{formatDate(d.date)}</div>
      <div className="flex items-center gap-2">
        <span className={`font-bold ${d.isBreaching ? 'text-red-600' : 'text-primary-500'}`}>
          {formatVariance(d.variance)}
        </span>
        <span className="text-gray-300">|</span>
        <span className="text-gray-400">thr {threshold}%</span>
      </div>
      {d.matched && (
        <div className="text-gray-500 mt-1 tabular-nums">
          Matched ({d.matched.count}): D {formatWeight(d.matched.dispatchWeight)}t &middot; R{' '}
          {formatWeight(d.matched.receiptWeight)}t
        </div>
      )}
      {d.dispatchWeight > 0 && (
        <div className="text-gray-400 mt-0.5 tabular-nums">
          Day total: D {formatWeight(d.dispatchWeight)}t &middot; R {formatWeight(d.receiptWeight)}t
        </div>
      )}
      {d.streakDay > 0 && (
        <div className="text-amber-600 mt-0.5 font-medium">Streak day {d.streakDay}</div>
      )}
    </div>
  );
};

// Bar colors based on breach status
const getBarColor = (entry: ChartPoint, threshold: number, t: ThemeTokens) => {
  if (entry.variance == null) return t.border;
  return Math.abs(entry.variance) > threshold ? t.danger : t.accent;
};

interface MonitorDetailModalProps {
  monitor: VarianceDashboardMonitor;
  onClose: () => void;
  onRefresh: () => void;
}

/**
 * A Dispatch or Receipt cell: the matched weight (what the variance is computed from) with the
 * day total and the unmatched loads underneath, or the day total alone for a day without
 * matched figures.
 */
const WeightCell = ({
  testId,
  matchedWeight,
  dayWeight,
  unmatched,
}: {
  testId: string;
  matchedWeight: number | undefined;
  dayWeight: VarianceResultRow['dispatch_weight'];
  unmatched: number | undefined;
}) => (
  <td data-testid={testId} className="px-3 py-2 text-right tabular-nums text-gray-500">
    {matchedWeight != null ? (
      <>
        {formatWeight(matchedWeight)}
        <span className="text-gray-300">t</span>
        <div className="text-[9px] text-gray-400 mt-0.5">matched</div>
        <div className="text-[9px] text-gray-300">
          Day total {formatWeight(dayWeight)}t{unmatched ? ` · ${unmatched} unmatched` : ''}
        </div>
      </>
    ) : (
      <>
        {formatWeight(dayWeight)}
        <span className="text-gray-300">t</span>
        <div className="text-[9px] text-gray-300 mt-0.5">Day total</div>
      </>
    )}
  </td>
);

const MonitorDetailModal = ({ monitor, onClose, onRefresh }: MonitorDetailModalProps) => {
  const t = useThemeTokens();
  const [results, setResults] = useState<VarianceResultRow[]>([]);
  const [summary, setSummary] = useState<VarianceResultSummary | null>(null);
  const [myAcks, setMyAcks] = useState<Record<number, unknown>>({});
  const [allAcks, setAllAcks] = useState<Record<number, VarianceAcknowledgment[]>>({});
  const [loading, setLoading] = useState(true);
  const [showAckModal, setShowAckModal] = useState<VarianceResultRow | null>(null);
  const [showAlertLog, setShowAlertLog] = useState(false);
  const [dayRange, setDayRange] = useState(7);

  const fetchData = useCallback(async () => {
    try {
      const [r, s, a] = await Promise.all([
        api.routeIntelligence.getResults(monitor.monitor_id, monitor.lookback_days || 40),
        api.routeIntelligence.getResultSummary(monitor.monitor_id),
        api.routeIntelligence.getMyAcknowledgments(monitor.monitor_id),
      ]);
      setResults((r.data as VarianceResultRow[]) || []);
      setSummary((s.data as VarianceResultSummary) || {});
      setMyAcks((a.data as { ackMap?: Record<number, unknown> } | undefined)?.ackMap || {});
    } catch {
    } finally {
      setLoading(false);
    }
  }, [monitor.monitor_id, monitor.lookback_days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fn = async () => {
      const breach = results.filter((r) => r.is_breaching);
      const data: Record<number, VarianceAcknowledgment[]> = {};
      await Promise.all(
        breach.map(async (r) => {
          try {
            const res = await api.routeIntelligence.getAcknowledgments(r.result_id);
            data[r.result_id] = (res.data as VarianceAcknowledgment[]) || [];
          } catch {
            data[r.result_id] = [];
          }
        })
      );
      setAllAcks(data);
    };
    if (results.length > 0) fn();
  }, [results]);

  const handleAckSaved = () => {
    setShowAckModal(null);
    fetchData();
    onRefresh();
  };

  const filteredResults = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - dayRange);
    return results.filter((r) => new Date(r.result_date) >= cutoff);
  }, [results, dayRange]);

  const chartData = useMemo((): ChartPoint[] => {
    return [...filteredResults]
      .filter(hasDayData)
      .sort((a, b) => new Date(a.result_date).getTime() - new Date(b.result_date).getTime())
      .map((r) => ({
        date: r.result_date,
        dateLabel: formatDateShort(r.result_date),
        variance:
          r.variance_percent != null
            ? parseFloat(parseFloat(String(r.variance_percent)).toFixed(2))
            : null,
        isBreaching: r.is_breaching,
        streakDay: r.streak_day,
        dispatchWeight: Number(r.dispatch_weight) || 0,
        receiptWeight: Number(r.receipt_weight) || 0,
        matched: rowMatched(r),
      }));
  }, [filteredResults]);

  const threshold = parseFloat(String(monitor.variance_threshold));
  const state = getVarianceState(summary?.currentStreak || 0, monitor.streak_days_to_alert);
  const breachInRange = filteredResults.filter((r) => r.is_breaching).length;
  const dataInRange = filteredResults.filter(hasDayData).length;

  const breachDayNumber = useMemo(() => {
    const map: Record<number, number> = {};
    let c = 0;
    [...results]
      .sort((a, b) => new Date(a.result_date).getTime() - new Date(b.result_date).getTime())
      .forEach((r) => {
        if (r.is_breaching) {
          c++;
          map[r.result_id] = c;
        }
      });
    return map;
  }, [results]);

  const yDomain = useMemo(() => {
    if (!chartData.length) return [-5, 5];
    const vals = chartData.map((d) => d.variance).filter((v): v is number => v != null);
    if (!vals.length) return [-5, 5];
    const mn = Math.min(...vals, -threshold),
      mx = Math.max(...vals, threshold);
    const p = Math.max((mx - mn) * 0.2, 1);
    return [Math.floor(mn - p), Math.ceil(mx + p)];
  }, [chartData, threshold]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg border border-gray-200 w-full max-w-5xl max-h-[92vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">{monitor.grouping_name}</h2>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Variance analysis &middot; Acknowledgment tracking
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAlertLog(true)}
              className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors"
            >
              <Mail className="w-3 h-3" /> Alert Log
            </button>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-4 gap-px bg-gray-100 border-b border-gray-100 flex-shrink-0">
          {[
            { label: 'Streak', value: `${summary?.currentStreak || 0}d`, color: state.accent },
            { label: 'Threshold', value: `${threshold.toFixed(1)}%`, color: 'var(--text-strong)' },
            {
              label: 'Breaches',
              value: `${breachInRange} / ${dataInRange}`,
              color: breachInRange > 0 ? 'var(--danger)' : 'var(--text-strong)',
            },
            {
              label: 'Last Alert',
              value: summary?.lastAlert ? formatDate(summary.lastAlert.sent_at) : 'Never',
              color: 'var(--text-muted)',
            },
          ].map((k, i) => (
            <div key={i} className="bg-surface px-4 py-2.5 text-center">
              <div className="text-sm font-bold tabular-nums" style={{ color: k.color }}>
                {k.value}
              </div>
              <div className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">
                {k.label}
              </div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="px-5 pt-4 pb-2 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-gray-500">Variance Trend</span>
            <div className="flex items-center gap-0.5 bg-gray-100 rounded-md p-0.5">
              {DAY_RANGES.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setDayRange(r.value)}
                  className={`px-2.5 py-1 text-[10px] font-semibold rounded transition-all ${
                    dayRange === r.value
                      ? 'bg-surface text-slate-800 shadow-sm'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          {loading ? (
            <div className="h-48 bg-gray-50 animate-pulse rounded" />
          ) : chartData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-xs text-gray-300">
              No data for selected range
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 8, right: 12, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={t.gridSubtle} vertical={false} />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fontSize: 10, fill: t.axisLabel }}
                  axisLine={{ stroke: t.grid }}
                  tickLine={false}
                />
                <YAxis
                  domain={yDomain}
                  tick={{ fontSize: 10, fill: t.axisLabel }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  content={<ChartTooltip threshold={threshold} />}
                  cursor={{ fill: t.cursor }}
                />
                <ReferenceLine
                  y={threshold}
                  stroke={t.danger}
                  strokeDasharray="4 4"
                  strokeWidth={1}
                  strokeOpacity={0.5}
                  label={{
                    value: `+${threshold}%`,
                    position: 'right',
                    fontSize: 9,
                    fill: t.danger,
                  }}
                />
                <ReferenceLine
                  y={-threshold}
                  stroke={t.danger}
                  strokeDasharray="4 4"
                  strokeWidth={1}
                  strokeOpacity={0.5}
                  label={{
                    value: `-${threshold}%`,
                    position: 'right',
                    fontSize: 9,
                    fill: t.danger,
                  }}
                />
                <ReferenceLine y={0} stroke={t.grid} strokeWidth={1} />
                <Bar dataKey="variance" radius={[3, 3, 0, 0]} maxBarSize={32}>
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill={getBarColor(entry, threshold, t)} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-primary-500" />
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-6 h-6 mx-auto mb-2 text-gray-200" />
              <p className="text-xs text-gray-400">No results. Run the monitor.</p>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="border-b border-gray-200">
                  {[
                    'Date',
                    'Dispatch',
                    'Receipt',
                    'Variance',
                    'Status',
                    'Streak',
                    'Acknowledged',
                    'Action',
                  ].map((h, i) => (
                    <th
                      key={h}
                      className={`px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider ${i >= 1 && i <= 3 ? 'text-right' : i >= 4 ? 'text-center' : 'text-left'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((r) => {
                  if (!hasDayData(r))
                    return (
                      <tr key={r.result_id} className="border-b border-gray-50">
                        <td className="px-3 py-1.5 text-gray-400">{formatDate(r.result_date)}</td>
                        <td
                          colSpan={7}
                          className="px-3 py-1.5 text-center text-gray-300 text-[10px] italic"
                        >
                          No data
                        </td>
                      </tr>
                    );
                  const rowState = r.is_breaching
                    ? r.streak_day >= (monitor.streak_days_to_alert ?? Infinity)
                      ? varianceStates.alert
                      : varianceStates.watch
                    : varianceStates.normal;
                  const isAcked = !!myAcks[r.result_id];
                  const acks = allAcks[r.result_id] || [];
                  const matched = rowMatched(r);
                  return (
                    <tr
                      key={r.result_id}
                      data-testid="variance-result-row"
                      className={`border-b border-gray-50 transition-colors ${r.is_breaching ? rowState.rowBg : 'hover:bg-gray-50/50'}`}
                    >
                      <td className="px-3 py-2">
                        <span className="text-slate-800 font-medium">
                          {formatDate(r.result_date)}
                        </span>
                        {r.is_breaching && (breachDayNumber[r.result_id] ?? 0) > 1 && (
                          <div className="text-[9px] text-gray-400 mt-0.5">
                            Breach #{breachDayNumber[r.result_id]}
                          </div>
                        )}
                      </td>
                      <WeightCell
                        testId="dispatch-cell"
                        matchedWeight={matched?.dispatchWeight}
                        dayWeight={r.dispatch_weight}
                        unmatched={matched?.unmatchedDispatches}
                      />
                      <WeightCell
                        testId="receipt-cell"
                        matchedWeight={matched?.receiptWeight}
                        dayWeight={r.receipt_weight}
                        unmatched={matched?.unmatchedReceipts}
                      />
                      <td
                        className="px-3 py-2 text-right tabular-nums font-bold"
                        style={{ color: rowState.accent }}
                      >
                        {formatVariance(r.variance_percent)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${rowState.badgeBg} ${rowState.badgeText} border ${rowState.badgeBorder}`}
                        >
                          {React.createElement(rowState.icon, { className: 'w-2.5 h-2.5' })}{' '}
                          {rowState.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {r.is_breaching ? (
                          <span className="font-semibold" style={{ color: rowState.accent }}>
                            D{r.streak_day}
                          </span>
                        ) : (
                          <span className="text-gray-200">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {r.is_breaching && acks.length > 0 ? (
                          <div className="space-y-0.5">
                            {acks.map((a) => (
                              <div
                                key={a.acknowledgment_id}
                                className="flex items-center justify-center gap-1 text-[10px]"
                                title={a.note ?? undefined}
                              >
                                <CheckCircle className="w-2.5 h-2.5 text-primary-500" />
                                <span className="text-gray-500 truncate max-w-[80px]">
                                  {a.user_name}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : r.is_breaching ? (
                          <span className="text-[10px] text-gray-300">Pending</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {r.is_breaching && !isAcked && (
                            <button
                              onClick={() => setShowAckModal(r)}
                              className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium text-primary-500 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded transition-colors"
                            >
                              <MessageSquare className="w-2.5 h-2.5" /> Ack
                            </button>
                          )}
                          {r.is_breaching && isAcked && (
                            <CheckCircle className="w-3.5 h-3.5 text-primary-500" />
                          )}
                          {r.alert_sent && (
                            <span title="Alert sent" aria-label="Alert sent">
                              <Mail className="w-3 h-3 text-amber-400" />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showAckModal && (
        <AcknowledgeModal
          result={showAckModal}
          monitor={monitor}
          onClose={() => setShowAckModal(null)}
          onSaved={handleAckSaved}
        />
      )}
      {showAlertLog && <AlertLogModal monitor={monitor} onClose={() => setShowAlertLog(false)} />}
    </div>
  );
};

export default MonitorDetailModal;
